import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from '../../../../ui/toast'; // Assuming you are using react-toastify for notifications
import {
  deleteCandidate,
  getCandidateList,
} from '../../../../utils/api_service/candidate';
import {
  addEmployeesWithCsv,
  createEmployee,
  deleteEmployee,
  editEmployee,
  getEmployee,
  getEmployeeList,
} from '../../../../utils/api_service/employees';
import {
  createJobTitle,
  getJobTitleList,
} from '../../../../utils/api_service/job_title';
import { getOrganizationUnitList } from '../../../../utils/api_service/organization_unit';
import { fetchUserGroups } from '../../../../utils/redux_store/features/user_group_reducer';
import { fetchUserRoles } from '../../../../utils/redux_store/features/user_role_reducer';
import { RootState, store } from '../../../../utils/redux_store/store';
import {
  EmployeeUser,
  JobTitle,
  OrganizationUser,
  SingleEmployee,
} from '../../../../utils/types/admin_types';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { OrganizationUnit } from '../../OrganizationSettings/TreeStructure';

export const useEmployeeManagement = () => {
  const [employeeUserList, setEmployeeUserList] = useState<EmployeeUser[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [organizationUnitList, setOrganizationUnitList] = useState<
    OrganizationUnit[]
  >([]);
  const [jobTitleList, setJobTitleList] = useState<JobTitle[]>([]);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
  const [editedEmployee, setEditedEmployee] =
    useState<EmployeeUser | null>(null);
  const [singleEmployee, setSingleEmployee] =
    useState<SingleEmployee | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasShownError, setHasShownError] = useState(false);
  const [loading, setLoading] = useState(false);
  const userGroup = useSelector(
    (state: RootState) => state.userGroups.userGroupList,
  );
  const userRole = useSelector(
    (state: RootState) => state.userRoles.userRoleList,
  );

  const fetchEmployees = (pageNumber: number) => {
    setLoading(true);

    getEmployeeList(searchTerm, pageNumber, 10)
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errorData) => {
            if (!hasShownError) {
              toast.error(errorData.error, { position: 'bottom-center' });
              setHasShownError(true);
            }
            throw new Error(errorData.error);
          });
        }
        return response.json();
      })
      .then((data) => {
        setEmployeeUserList(data.employee);
        setTotalRecords(data.numberOfEmployee);
        setTotalPages(data.pages);
        setHasShownError(false);
      })
      .catch((error) => {
        if (!hasShownError) {
          setHasShownError(true);
        }
        setEmployeeUserList([]);
        setTotalPages(1);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getOrganizationUnitList().then(setOrganizationUnitList);
    getJobTitleList().then(setJobTitleList);
    store.dispatch(fetchUserGroups());
    store.dispatch(fetchUserRoles());
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => {
      setPageNumber(1);
      fetchEmployees(pageNumber);
    }, 600);

    return () => clearTimeout(delay);
  }, [searchTerm]);

  useEffect(() => {
    fetchEmployees(pageNumber);
  }, [pageNumber]);

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
    setHasShownError(false);
  };

  const deleteUser = async (id: string) => {
    try {
      const response = await deleteEmployee(id);
      if (response.ok) {
        setEmployeeUserList((prev) =>
          prev.filter((employee) => employee.id !== id),
        );
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error deleting employee: ' + error, {
        position: 'bottom-center',
      });
    }
  };

  const openEditModal = async (employee: EmployeeUser) => {
    const response = await getEmployee(employee.id);
    setSingleEmployee(response);
    setEditedEmployee(employee);
    setEditModalVisible(true);
  };

  const inviteEmployee = async (
    email: string,
    firstname: string,
    lastname: string,
    selectedJobTitle: string,
    organizationUnit: string,
    reportingManager: string | undefined,
  ) => {
    try {
      const response = await createEmployee(
        email,
        firstname,
        lastname,
        reportingManager,
        organizationUnit,
        selectedJobTitle,
      );
      if (response.ok) {
        const data = await response.json();
        setEmployeeUserList((prev) => [...prev, data]);
        setInviteModalVisible(false);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error adding employee: ' + error, {
        position: 'bottom-center',
      });
    }
  };

  const inviteBulkEmployees = async (csvFile: File) => {
    try {
      const response = await addEmployeesWithCsv(csvFile);
      if (response.ok) {
        const data = await response.json();
        toast.success('Successfully uploaded file!', {
          position: 'bottom-center',
        });
        setEmployeeUserList((prev) => [...prev, ...data]);
        setBulkUploadModalVisible(false);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error adding employee: ' + error, {
        position: 'bottom-center',
      });
    }
  };

  const onAddNewJobTitle = (title: string) => {
    createJobTitle(title).then((response) => {
      if (response.id) {
        setJobTitleList((prev) => [...prev, response]);
      }
    });
  };

  const editUser = async (
    employee: EmployeeUser,
    groups: SelectOptions[],
    roles: SelectOptions[],
    selectedJobTitle: string,
    organizationUnit: string,
    reportingManager: string | undefined,
  ) => {
    const groupList = groups.map((g) => g.value);
    const roleList = roles.map((r) => r.value);

    try {
      const response = await editEmployee(
        roleList,
        groupList,
        employee.id,
        reportingManager,
        organizationUnit,
        selectedJobTitle,
      );
      if (response.ok) {
        setEditModalVisible(false);
        const updatedUser = {
          ...employee,
          reportingManager,
          organizationUnit,
          selectedJobTitle,
          groups: userGroup.filter((g) => groupList.includes(g.id)),
          roles: userRole.filter((r) => roleList.includes(r.id)),
        };
        setEmployeeUserList((prev) =>
          prev.map((e) => (e.id === employee.id ? updatedUser : e)),
        );
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error editing employee: ' + error, {
        position: 'bottom-center',
      });
    }
  };
  const onPageChange = (page: number) => {
    setPageNumber(page);
  };
  return {
    employeeUserList,
    loading,
    pageNumber,
    setPageNumber,
    totalPages,
    searchTerm,
    deleteUser,
    openEditModal,
    editedEmployee,
    editModalVisible,
    setEditModalVisible,
    inviteModalVisible,
    setInviteModalVisible,
    inviteEmployee,
    inviteBulkEmployees,
    bulkUploadModalVisible,
    setBulkUploadModalVisible,
    jobTitleList,
    onAddNewJobTitle,
    organizationUnitList,
    onPageChange,
    editUser,
    userGroup,
    userRole,
    singleEmployee,
    handleSearchTermChange,
    setSingleEmployee,
    setEditedEmployee,
    setEmployeeUserList,
    totalRecords,
  };
};
